import { useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { ArrowRight, AlarmClock, Compass, RotateCcw, ShieldCheck, Lock, CheckCircle2, MessageCircle, ListChecks, UploadCloud, Users, Navigation } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { Checkbox } from "../../components/ui/checkbox";
import { Logo } from "../../components/joe/Badges";
import { BRAND, CREW } from "../../lib/brand";
import { api, store, errMsg } from "../../lib/api";

const DISCLOSURE = [
  "AskJoe-AI provides educational information, document organization, preliminary issue spotting, and referrals. AskJoe-AI is not a law firm and does not replace advice from an attorney, CPA, HUD-approved housing counselor, licensed real-estate professional, mortgage servicer, tax authority, or other appropriately licensed professional.",
  "Results depend upon the accuracy and completeness of the information provided.",
  "If you have received a summons, complaint, notice of sale, tax deed notice, bankruptcy document, or other time-sensitive legal notice, upload it immediately and consider contacting a qualified attorney.",
];
const STEPS = [[MessageCircle, "Understand your situation"], [ListChecks, "Get answers & options"], [UploadCloud, "Upload documents"], [Users, "Connect to a specialist"], [Navigation, "Take the next step"]];

const Action = ({ icon: Icon, label, onClick, cls, testId, delay }) => (
  <button data-testid={testId} onClick={onClick} style={{ animationDelay: `${delay}ms` }}
    className={`rise tap group flex min-h-[60px] w-full items-center justify-between rounded-2xl px-5 py-4 text-left font-display text-lg font-bold uppercase tracking-wide shadow-sm ${cls}`}>
    <span className="flex items-center gap-3"><Icon className="h-5 w-5" />{label}</span>
    <ArrowRight className="h-5 w-5 transition-transform group-hover:translate-x-1" />
  </button>
);

const Crew = () => (
  <section className="relative z-10 bg-white py-14" data-testid="crew-section">
    <div className="mx-auto max-w-6xl px-5">
      <h2 className="text-center font-display text-3xl font-extrabold uppercase text-brand-navy sm:text-4xl">Let Joe and his friends help you</h2>
      <p className="mx-auto mt-2 max-w-2xl text-center text-slate-600">Each friend guides a different part of your review inside AskJoe-AI.</p>
      <div className="mt-8 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-7">
        {Object.entries(CREW).map(([k, c], i) => (
          <div key={k} data-testid={`crew-${k}`} style={{ animationDelay: `${i * 60}ms` }} className="rise overflow-hidden rounded-2xl border border-slate-200 bg-white text-center shadow-sm">
            <img src={c.img} alt={c.name} className="aspect-square w-full object-cover" />
            <div className="px-2 py-1.5 text-white" style={{ background: c.color }}><p className="font-display text-lg font-extrabold uppercase leading-none">{c.name}</p><p className="text-[10px] font-semibold uppercase">{c.role.replace("The ", "")}</p></div>
            <p className="p-2 text-xs leading-snug text-slate-600">{c.desc}</p>
          </div>
        ))}
      </div>
    </div>
  </section>
);

export default function Welcome() {
  const nav = useNavigate();
  const [sp] = useSearchParams();
  const [entry, setEntry] = useState(null);
  const [agree, setAgree] = useState(false);
  const [busy, setBusy] = useState(false);
  const hasVisit = !!store.visit;

  const begin = async (e) => {
    if (store.caseToken) return nav("/review");
    if (!store.visit) {
      try {
        const { data } = await api.post("/track/visit", { code: "DIRECT", src: "direct", visitor_id: store.visitor, landing: window.location.pathname, utm: {} });
        store.visit = data.visit_id;
        store.visitor = data.visitor_id;
      } catch (err) { return toast.error(errMsg(err)); }
    }
    setEntry(e);
  };
  const start = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/intake/start", { visit_id: store.visit, entry, disclosure_accepted: agree });
      store.caseToken = data.case_token;
      nav(entry === "options" ? "/review?mode=options" : "/review");
    } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-sky-100 via-white to-white">
      <header className="relative z-10 mx-auto flex max-w-6xl items-center justify-between px-5 py-4">
        <Logo className="h-12 sm:h-14" />
        <Link to="/login" data-testid="header-signin-link" className="rounded-full border-2 border-brand-navy px-4 py-1.5 text-sm font-bold text-brand-navy hover:bg-brand-navy hover:text-white">Sign in</Link>
      </header>
      <main className="relative z-10 mx-auto grid max-w-6xl gap-8 px-5 pb-12 pt-2 lg:grid-cols-[1.05fr_.95fr] lg:gap-12">
        <section>
          <p className="rise font-display text-lg font-bold uppercase tracking-wide text-brand-orange" data-testid="welcome-eyebrow">Welcome to AskJoe-AI™</p>
          <h1 className="rise mt-1 font-display text-5xl font-extrabold uppercase leading-[.92] sm:text-6xl lg:text-7xl" style={{ animationDelay: "60ms" }}>
            <span className="text-brand-red">Sell your home</span><br />
            <span className="mr-2 inline-block -rotate-3 rounded bg-brand-yellow px-2 text-4xl text-brand-navy sm:text-5xl">or</span><span className="text-brand-green">keep your home!</span>
          </h1>
          <p className="rise mt-4 font-display text-2xl font-bold text-brand-navy sm:text-3xl" style={{ animationDelay: "120ms" }} data-testid="welcome-headline">
            Understand Your Situation. Know Your Options. Take the Next Step.
          </p>
          <p className="rise mt-3 max-w-xl text-base leading-relaxed text-slate-600" style={{ animationDelay: "160ms" }}>
            If you're behind on your mortgage, facing foreclosure, dealing with delinquent property taxes, concerned about a tax deed sale, or need to sell your property quickly, Joe will help you organize the facts and understand potential next steps.
          </p>
          {sp.get("invalid") && <p className="mt-4 rounded-xl bg-red-50 p-3 text-sm text-red-800" data-testid="invalid-referral-msg">That referral link isn't active. Please contact the person who shared it with you.</p>}
          {!hasVisit && !store.caseToken && (
            <p className="mt-5 flex items-start gap-2 rounded-xl border border-slate-200 bg-white p-3 text-sm text-slate-600" data-testid="direct-entry-msg">
              <Lock className="mt-0.5 h-4 w-4 shrink-0" /> Got a link or QR code from an AskJoe team member or partner? Please use it so they can help you. Otherwise, you can start right here. Already started? Sign in to continue.
            </p>
          )}
          <div className="mt-6 grid max-w-xl gap-3">
            <Action testId="btn-start-review" icon={ShieldCheck} label="Start my homeowner review" cls="bg-brand-orange text-white hover:brightness-110" onClick={() => begin("start")} delay={220} />
            <Action testId="btn-urgent" icon={AlarmClock} label="I have an urgent sale or court date" cls="bg-brand-red text-white hover:brightness-110" onClick={() => begin("urgent")} delay={280} />
            <Action testId="btn-options" icon={Compass} label="I want to understand my options" cls="bg-brand-navy text-white hover:brightness-125" onClick={() => begin("options")} delay={340} />
            <Action testId="btn-already-started" icon={RotateCcw} label="I already started my review" cls="border-2 border-brand-navy bg-white text-brand-navy" onClick={() => nav(store.caseToken ? "/case" : "/login")} delay={400} />
          </div>
          <div className="mt-5 grid max-w-xl grid-cols-2 gap-x-4 gap-y-1.5 text-sm font-semibold text-brand-navy">
            {["No obligation", "Confidential", "Your information stays private", "Get connected to the right specialist"].map((t) => <p key={t} className="flex items-center gap-1.5"><CheckCircle2 className="h-4 w-4 shrink-0 text-brand-blue" />{t}</p>)}
          </div>
        </section>
        <aside className="rise relative lg:self-center" style={{ animationDelay: "200ms" }}>
          <div className="overflow-hidden rounded-[1.5rem] border-4 border-white shadow-xl">
            <img src={BRAND.team} alt="Joe the Homeowner Guide and his friends" className="w-full" data-testid="joe-hero-img" />
          </div>

        </aside>
      </main>
      <div className="relative z-10 mt-6 bg-brand-yellow py-3 text-center font-display text-2xl font-extrabold italic text-brand-navy sm:text-3xl">It's Easy. It's Private. It's <span className="text-brand-red underline">Free</span> to Get Started!</div>
      <Crew />
      <section className="relative z-10 bg-brand-navy text-white">
        <div className="mx-auto grid max-w-6xl grid-cols-2 gap-4 px-5 py-8 sm:grid-cols-5">
          {STEPS.map(([Icon, l]) => <div key={l} className="text-center"><Icon className="mx-auto h-9 w-9" /><p className="mt-2 font-display text-sm font-bold uppercase">{l}</p></div>)}
        </div>
        <p className="pb-6 text-center text-xs text-white/60">{BRAND.tagline} AskJoe-AI is not a law firm.</p>
      </section>
      <Dialog open={!!entry} onOpenChange={(o) => !o && setEntry(null)}>
        <DialogContent className="max-w-lg" data-testid="disclosure-dialog">
          <DialogHeader><DialogTitle className="font-display text-2xl uppercase text-brand-navy">Important Disclosure</DialogTitle></DialogHeader>
          <div className="space-y-3 text-sm leading-relaxed text-slate-700">{DISCLOSURE.map((p) => <p key={p.slice(0, 20)}>{p}</p>)}</div>
          <label className="mt-2 flex cursor-pointer items-center gap-3 rounded-xl bg-amber-50 p-3 font-semibold">
            <Checkbox data-testid="disclosure-checkbox" checked={agree} onCheckedChange={(v) => setAgree(!!v)} /> I understand and want to continue.
          </label>
          <button data-testid="disclosure-continue-btn" disabled={!agree || busy} onClick={start}
            className="tap mt-2 h-12 w-full rounded-xl bg-brand-orange font-display text-lg font-bold uppercase tracking-wide text-white disabled:opacity-40">
            {busy ? "Starting…" : "Continue"}
          </button>
        </DialogContent>
      </Dialog>
    </div>
  );
}
